import { message } from 'antd';
import { IntlShape } from 'react-intl';
// moment-timezone will be dynamically imported
import { handleErrorNotification } from 'Common/utils';

// eslint-disable-next-line import/prefer-default-export
export interface TimezoneOption {
  key: string;
  value: string;
}

export const getMomentTimezones = (): never => {
  throw new Error(
    'getMomentTimezones must be called as getMomentTimezonesAsync'
  );
};

export const getMomentTimezonesAsync = async (): Promise<TimezoneOption[]> => {
  const dayjs = (await import(/* webpackChunkName: "dayjs" */ 'dayjs')).default;
  const timezone = (
    await import(
      /* webpackChunkName: "dayjs-timezone" */ 'dayjs/plugin/timezone'
    )
  ).default;
  const utc = (
    await import(/* webpackChunkName: "dayjs-utc" */ 'dayjs/plugin/utc')
  ).default;
  dayjs.extend(timezone);
  dayjs.extend(utc);

  const timeZones = Intl.supportedValuesOf('timeZone');
  return timeZones
    .map((tz: string) => {
      try {
        return {
          key: tz,
          value: `${tz} GMT${dayjs().tz(tz).format('Z')}`,
        };
      } catch (error) {
        return null;
      }
    })
    .filter((tz): tz is TimezoneOption => tz !== null);
};

export const mapProductToOption = (p: { id: number; name: string }) => ({
  key: String(p.id),
  label: p.name,
  value: p.id,
});

export const createLengthValidationRule = (
  intl: IntlShape,
  length: number
) => ({
  max: length,
  message: intl.formatMessage(
    {
      defaultMessage: 'Max. {len} characters are allowed',
    },
    { len: length }
  ),
});

//Remove Numbers, Brackets and ExtraSpaces from Error
export const formatError = (description: string): string =>
  description
    .replace(/\[[^\]]*\]/g, '')
    .replace(/\d+/g, '')
    .replace(/\s+/g, ' ')
    .trim();

export const handleAccountingError = (error: any, intl: IntlShape) => {
  if (error?.error?.key === 'accounting.duplicate_accounts') {
    if (
      error?.error?.description.includes('multiple times') ||
      error?.error?.description.includes('already in use')
    ) {
      message.error(
        intl.formatMessage({
          defaultMessage:
            'Account number is used multiple times in the request',
        })
      );
    } else if (error?.error?.description.includes('default one')) {
      message.error(
        intl.formatMessage({
          defaultMessage:
            'The account number is a default one and cannot be used',
        })
      );
    }
  } else {
    handleErrorNotification({
      error,
      lang: intl.locale.toLowerCase(),
    });
  }
};

type LatestSubscriptionQueryState = {
  status?: string;
  data?: { activation_allowed?: boolean } | null;
};

/**
 * activation_allowed is false when the subscription is already active OR when a
 * subscription contract is accepted by a customer whose plan starts in the
 * future (they still show as "trial" on Chargebee). We treat the contract as
 * accepted — i.e. hide activation entry points such as the "Activate now" CTA
 * and the Subscribe tab — unless the latest-subscription query has succeeded
 * AND activation_allowed is true.
 *
 * Accepts either a `useQuery` result or a `getQueryState` snapshot; both expose
 * `.status` and `.data`.
 */
export const getIsContractAccepted = (
  latestSubscription: LatestSubscriptionQueryState | undefined | null
): boolean =>
  !(
    latestSubscription?.status === 'success' &&
    latestSubscription?.data?.activation_allowed
  );

export const extractAccountNumbers = (text: string) => {
  const regex = /['"]?(\d+)['"]?/g;
  const numbers = [];
  let match;

  /* eslint-disable-next-line no-cond-assign */
  while ((match = regex.exec(text)) !== null) {
    numbers.push(match[1]);
  }

  return numbers;
};
